<template>
    <div
        role="button"
        class="ticket-packages-item"
        :class="`ticket-packages-item--${type}`"
        @click="openMoreInfo"
    >
        <div class="ticket-packages-item-content">
            <div class="ticket-packages-item-icon">
                <svg-icon :name="type" />
            </div>

            <div class="item-content-left">
                <div class="ticket-packages-item-header">
                    <h2 class="ticket-packages-item-title">
                        {{ translatedTitle }}
                    </h2>
                    <span class="badge" :class="`badge-${type}`"
                        >{{ trans.tickets.sector }} {{ sector }}
                        {{
                            rows ? ` | ${trans.tickets.row} ${rows}` : ""
                        }}</span
                    >

                    <ticket-packages-type
                        :type="ticket_type"
                        :trans="trans.seat"
                    />
                </div>

                <div class="ticket-packages-item-info">
                    <span class="date"
                        ><strong>{{ date }}</strong> - {{ dayOfWeek }}</span
                    >
                    <span class="group">{{ group }}</span>
                </div>

                <div class="ticket-packages-item-type">
                    <ticket-packages-type
                        :type="ticket_type"
                        :trans="trans.seat"
                    />
                </div>

                <div class="ticket-packages-item-includes-flag">
                    <div
                        class="ticket-packages-item-includes"
                        v-if="includes.length > 0"
                    >
                        <small class="label">{{
                            trans.tickets.package_includes
                        }}</small>
                        <span class="compact-icons">
                            <img
                                class="include-icon"
                                :src="include.icon"
                                :alt="include.name"
                                :title="include.name"
                                v-for="include in includes.slice(0, 3)"
                                :key="include.icon"
                            />
                            <strong
                                v-if="includes.length > 3"
                                class="compact-icons-more"
                                >+{{ includes.length - 3 }}</strong
                            >
                        </span>
                    </div>

                    <ticket-packages-flag
                        :code="flag.code"
                        :title="flag.title"
                        :text="flag.text"
                    />
                </div>

                <div
                    class="ticket-packages-item-included-free"
                    v-if="included_free && included_free.length"
                    v-for="include in included_free"
                    :key="include.title"
                >
                    <div class="included-free-content">
                        <strong>{{ trans.tickets.included }}</strong>
                        <span>{{ include.title }}</span>
                    </div>

                    <figure class="included-free-image">
                        <img :src="include.image" :alt="include.title" />
                    </figure>
                </div>
            </div>

            <div class="item-content-right">
                <ticket-packages-price
                    :price="price"
                    :from="price_old"
                    :trans="trans.tickets"
                />

                <button
                    class="button button-primary button-action"
                    @click.prevent="navigateToDetails"
                    :disabled="!isPurchasable"
                >
                    {{ actionLabel }}
                </button>

                <ticket-packages-flag
                    :code="flag.code"
                    :title="flag.title"
                    :text="flag.text"
                />
            </div>
        </div>

        <div
            class="ticket-packages-item-included-free"
            v-if="included_free && included_free.length"
            v-for="include in included_free"
            :key="include.title"
        >
            <div class="included-free-content">
                <strong>{{ trans.tickets.included }}</strong>
                <span>{{ include.title }}</span>
            </div>

            <figure class="included-free-image">
                <img :src="include.image" :alt="include.title" />
            </figure>
        </div>

        <div class="ticket-packages-item-footer">
            <div class="footer-left" v-if="!moreInfo && includes.length > 0">
                <small class="footer-label">{{
                    trans.tickets.package_includes
                }}</small>
                <span class="compact-icons">
                    <img
                        class="include-icon"
                        :src="include.icon"
                        :alt="include.name"
                        :title="include.name"
                        v-for="include in includes.slice(0, 3)"
                        :key="include.icon"
                    />
                    <strong
                        v-if="includes.length > 3"
                        class="compact-icons-more"
                        >+{{ includes.length - 3 }}</strong
                    >
                </span>
            </div>
            <div class="footer-right" v-if="!moreInfo">
                <a href="#" class="link-more" @click.prevent>
                    {{
                        moreInfo
                            ? trans.tickets.see_less
                            : trans.tickets.see_more
                    }}
                </a>
            </div>

            <div class="more-info" v-if="moreInfo">
                <small class="footer-label" v-if="includes.length > 0">{{
                    trans.tickets.package_includes
                }}</small>
                <div class="includes-list" v-if="includes.length > 0">
                    <span v-for="include in includes" :key="include.icon">
                        <img
                            class="include-icon"
                            :src="include.icon"
                            :alt="include.name"
                            :title="include.name"
                            :key="include.icon"
                        />
                        {{ include.name }}
                    </span>
                </div>

                <small class="footer-label">{{
                    trans.tickets.your_location
                }}</small>
                <map-sambadrome :location="type" :sector="sector" />

                <div class="footer-actions">
                    <button
                        class="button button-primary button-outline"
                        @click.stop="closeMoreInfo"
                    >
                        {{ trans.tickets.close }}
                    </button>
                    <button
                        class="button button-primary"
                        @click="navigateToDetails"
                        :disabled="!isPurchasable"
                    >
                        {{ actionLabel }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.compact-icons .include-icon,
.includes-list .include-icon {
    width: 20px;
    height: 20px;
    max-width: 20px;
    flex-shrink: 0;
}
</style>

<script>
import MapSambadrome from "../../map/MapSambadrome.vue";
import TicketPackagesFlag from "./TicketPackagesFlag.vue";
import TicketPackagesPrice from "./TicketPackagesPrice.vue";
import TicketPackagesType from "./TicketPackagesType.vue";

export default {
    components: {
        TicketPackagesFlag,
        TicketPackagesPrice,
        TicketPackagesType,
        MapSambadrome
    },
    props: {
        page: {
            type: Object,
            required: true
        },
        id: {
            type: Number,
            required: true
        },
        hash: {
            type: String,
            required: true
        },
        status: {
            type: String,
            required: true
        },
        soldout: {
            type: [Number, String],
            required: false,
            default: 0
        },
        status_text: {
            type: String,
            required: true
        },
        type: {
            type: String,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        location: {
            type: String,
            required: false
        },
        sector: {
            type: String,
            required: true
        },
        rows: {
            type: String,
            required: true
        },
        date: {
            type: String,
            required: true
        },
        dayOfWeek: {
            type: String,
            required: true
        },
        group: {
            type: String,
            required: true
        },
        price: {
            type: String,
            required: true
        },
        price_old: {
            type: String,
            required: false
        },
        ticket_type: {
            type: String,
            required: true
        },
        flag: {
            type: Object,
            required: true
        },
        includes: {
            type: Array,
            required: true
        },
        included_free: {
            type: Array,
            required: true
        },
        priceType: {
            type: String,
            required: true
        },
        lang: {
            type: String,
            required: true
        },
        trans: {
            type: Object,
            required: true
        },
        config: {
            type: Object,
            required: true
        },
        htmlId: {
            type: String,
            required: false
        }
    },
    data() {
        return {
            moreInfo: false
        };
    },
    computed: {
        translatedTitle() {
            return this.location || this.title;
        },
        isPurchasable() {
            return this.status === "enable";
        },
        actionLabel() {
            if (this.isPurchasable) {
                return this.trans.tickets.see_details;
            }
            if (this.status_text) {
                return this.status_text;
            }
            if (
                this.status === "disable" ||
                Number(this.soldout) === 1 ||
                Number(this.soldout) === 4
            ) {
                return this.trans.tickets.sold_out || "Sold out";
            }
            return this.trans.tickets.available_soon;
        }
    },
    methods: {
        openMoreInfo() {
            if (window.innerWidth >= 768) {
                this.navigateToDetails();
                return;
            }

            this.moreInfo = true;
        },
        closeMoreInfo() {
            this.moreInfo = false;
        },
        navigateToDetails() {
            const baseUrl = window.location.origin;
            const route = this.page.route || "";
            const hash = this.hash || "";
            const showItemsOnly = this.config.show_items_only || false;
            const componentAnchor = this.htmlId;

            const baseRoute = String(route).replace(/^\/+|\/+$/g, "");
            const ticketHash = String(hash).replace(/^\/+|\/+$/g, "");
            const pageUrl = new URL(`${baseUrl}/${baseRoute}/${ticketHash}`);

            const currentUrlParams = new URLSearchParams(
                window.location.search
            );
            currentUrlParams.forEach((value, key) => {
                pageUrl.searchParams.set(key, value);
            });

            if (showItemsOnly && componentAnchor) {
                const anchorValue = String(componentAnchor);
                pageUrl.hash = anchorValue.startsWith("#")
                    ? anchorValue.slice(1)
                    : anchorValue;
            }

            window.location.href = pageUrl.toString();
        }
    }
};
</script>
